import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSlot, scaffoldModuleTypes } from 'layout/utils-show-hide.js';
import { ISecondarySlotConfig, ISecondarySlotPropsMap } from 'redux-state/layout/types.layout.js';
import { IModuleConfig } from 'redux-state/layout/types.modules.js';
import { ModulePath } from '../../utilities/modulePath/modulePath.js';
import { IModuleConfigState, SecondaryModulesState } from './types.js';

const initialState: SecondaryModulesState = {
  secondaryMajor: [],
  secondaryMinor: [],
  secondaryRight: [],
};

/** Update module config in redux state with updated props */
const getRemovedModuleList = ({
  moduleList,
  dataUrl,
}: {
  moduleList: IModuleConfigState[];
  dataUrl: string | undefined;
}): IModuleConfigState[] | undefined => {
  // Find module with matching dataUrl in store
  const matchIndex = moduleList.findIndex((m) => m.data.dataUrl === dataUrl);
  /** Does data url exist in set? */
  if (matchIndex > -1) {
    const newModuleList = [...moduleList.slice(0, matchIndex), ...moduleList.slice(matchIndex + 1)];
    return newModuleList;
  }
  // dont mutate
  return undefined;
};

/** Update module config in redux state with updated props */
const getUpdatedModuleList = ({
  moduleList,
  dataUrl,
  module,
}: {
  moduleList: IModuleConfigState[];
  dataUrl: string | undefined;
  module: IModuleConfig;
}): IModuleConfigState[] | undefined => {
  // Find module with matching dataUrl in store
  const matchIndex = moduleList.findIndex((m) => m.data.dataUrl === dataUrl);
  /** Does data url exist in set? */
  if (matchIndex > -1) {
    const newModule: IModuleConfigState = { type: moduleList[matchIndex].type, data: module };
    const newModuleList = Object.assign([], moduleList, {
      [matchIndex]: newModule,
    });
    return newModuleList;
  }
  // dont mutate
  return undefined;
};

/** Updates state via new reference if has update otherwise just return (causes no new refresh) */
const getUpdatedState = ({
  state,
  param,
  update,
}: {
  state: SecondaryModulesState;
  param: keyof SecondaryModulesState;
  update: IModuleConfigState[] | undefined;
}) => {
  if (update) {
    return {
      ...state,
      ...{ [param]: update },
    };
  }
  return state;
};

/** also used by nextjs prototype */
export const convertSecondaryModules = (slots: ISecondarySlotPropsMap): SecondaryModulesState => {
  const secondaryMajor = scaffoldModuleTypes(getSlot(slots.major).modules);
  const secondaryMinor = scaffoldModuleTypes(getSlot(slots.minor).modules);
  const secondaryRight = scaffoldModuleTypes(getSlot(slots.right).modules);
  return {
    secondaryMajor,
    secondaryMinor,
    secondaryRight,
  };
};

export const isSecondaryModule = (modulePath: ModulePath): boolean => {
  switch (modulePath) {
    case ModulePath.SECONDARY_MAJOR:
    case ModulePath.SECONDARY_MINOR:
    case ModulePath.SECONDARY_RIGHT:
      return true;
    default:
      return false;
  }
};

/**
 * This is the first stage of transitioning all module data from layout into its own data structure
 * As / If this is used beyond secondary elements then these need to be added and properly handled
 * Used here src/layout/sections/secondary-section/secondary-layout.jsx
 *  const secondaryConfig = useSelector((state: IRepoState) => state.MODULES);
 */

export const secondaryModulesSlice = createSlice({
  name: 'modules',
  initialState,
  reducers: {
    REDUX_SECONDARY_MODULES_SET: (_, action: PayloadAction<ISecondarySlotConfig>) => {
      return convertSecondaryModules(action.payload.slots);
    },
    REDUX_SECONDARY_MODULES_UPDATE: (
      state,
      action: PayloadAction<{
        path: ModulePath;
        module: IModuleConfig;
        dataUrl: string | undefined;
      }>,
    ) => {
      const { dataUrl, path, module } = action.payload;
      if (dataUrl) {
        switch (path) {
          case ModulePath.SECONDARY_MAJOR: {
            const secondaryMajorModules = getUpdatedModuleList({ moduleList: state.secondaryMajor, dataUrl, module });
            const newState = getUpdatedState({ state, param: 'secondaryMajor', update: secondaryMajorModules });
            return newState;
          }
          case ModulePath.SECONDARY_MINOR: {
            const secondaryMinorModules = getUpdatedModuleList({ moduleList: state.secondaryMinor, dataUrl, module });
            const newState2 = getUpdatedState({ state, param: 'secondaryMinor', update: secondaryMinorModules });
            return newState2;
          }
          case ModulePath.SECONDARY_RIGHT: {
            const secondaryRightModules = getUpdatedModuleList({ moduleList: state.secondaryRight, dataUrl, module });
            const newState3 = getUpdatedState({ state, param: 'secondaryRight', update: secondaryRightModules });
            return newState3;
          }
          default:
            return state;
        }
      }
      return state;
    },
    REDUX_SECONDARY_MODULE_REMOVE: (
      state,
      action: PayloadAction<{
        path: ModulePath;
        dataUrl: string | undefined;
      }>,
    ) => {
      const { dataUrl, path } = action.payload;
      if (dataUrl) {
        switch (path) {
          case ModulePath.SECONDARY_MAJOR: {
            const secondaryMajorModules = getRemovedModuleList({ moduleList: state.secondaryMajor, dataUrl });
            const newState = getUpdatedState({ state, param: 'secondaryMajor', update: secondaryMajorModules });
            return newState;
          }
          case ModulePath.SECONDARY_MINOR: {
            const secondaryMinorModules = getRemovedModuleList({ moduleList: state.secondaryMinor, dataUrl });
            const newState2 = getUpdatedState({ state, param: 'secondaryMinor', update: secondaryMinorModules });
            return newState2;
          }
          case ModulePath.SECONDARY_RIGHT: {
            const secondaryRightModules = getRemovedModuleList({ moduleList: state.secondaryRight, dataUrl });
            const newState3 = getUpdatedState({ state, param: 'secondaryRight', update: secondaryRightModules });
            return newState3;
          }
          default:
            return state;
        }
      }
      return state;
    },
    REDUX_SECONDARY_MODULES_CLEAR: () => {
      return initialState;
    },
  },
});

export const {
  REDUX_SECONDARY_MODULES_SET,
  REDUX_SECONDARY_MODULES_UPDATE,
  REDUX_SECONDARY_MODULES_CLEAR,
  REDUX_SECONDARY_MODULE_REMOVE,
} = secondaryModulesSlice.actions;

export const secondaryModulesReducer = secondaryModulesSlice.reducer;
